import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchPowerTimeSeriesData,
  fetchPerformanceMetricsData,
  fetchStrokeData,
} from '../utils/chartDataFetchers';
import { telemetryService } from '../services/telemetry/telemetryService';
import { useSessionCache } from './useSessionData';

// Hook for power time series data (used by PowerAnalysisChart)
export const usePowerTimeSeriesData = (sessionId: string | null) => {
  return useQuery({
    queryKey: ['chart-data', 'power-time-series', sessionId],
    queryFn: () => fetchPowerTimeSeriesData(sessionId!),
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Hook for performance metrics data (used by PerformanceMetrics)
export const usePerformanceMetricsData = (sessionId: string | null) => {
  return useQuery({
    queryKey: ['chart-data', 'performance-metrics', sessionId],
    queryFn: () => fetchPerformanceMetricsData(sessionId!),
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Hook for stroke data (used by GateForceSignature)
export const useStrokeData = (sessionId: string | null, rowerPosition: number = 1) => {
  return useQuery({
    queryKey: ['chart-data', 'stroke-data', sessionId, rowerPosition],
    queryFn: () => fetchStrokeData(sessionId!, rowerPosition),
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Hook for comprehensive session data (used by multiple components)
export const useComprehensiveSessionData = (sessionId: string | null) => {
  return useQuery({
    queryKey: ['chart-data', 'comprehensive-session', sessionId],
    queryFn: () => telemetryService.getComprehensiveSessionData(sessionId!),
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Hook for GPS data (used by GPSTrackMap)
export const useGPSData = (sessionId: string | null) => {
  return useQuery({
    queryKey: ['chart-data', 'gps-data', sessionId],
    queryFn: async () => {
      if (!sessionId) return [];
      const comprehensiveData = await telemetryService.getComprehensiveSessionData(sessionId);
      return comprehensiveData?.gpsData || [];
    },
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Hook for session parameters (used by SessionParametersPanel)
export const useSessionParameters = (sessionId: string | null) => {
  return useQuery({
    queryKey: ['chart-data', 'session-parameters', sessionId],
    queryFn: async () => {
      if (!sessionId) return null;

      // Return default parameters that match what we set in parsePastedPeachData.js
      // This ensures physics calculations can work even when no parameters are stored
      return {
        id: 'default',
        session_id: sessionId,
        sweep_oar_length: 376.0, // Standard sweep oar length (cm)
        sweep_oar_inboard: 115.0, // Standard sweep oar inboard (cm)
        sweep_drive_start: 30.0, // Standard drive start force (kg)
        sweep_recovery: 15.0, // Standard recovery force (kg)
        scull_oar_length: 288.0, // Standard scull oar length (cm)
        scull_oar_inboard: 88.0, // Standard scull oar inboard (cm)
        scull_drive_start: 20.0, // Standard scull drive start force (kg)
        scull_recovery: 10.0, // Standard scull recovery force (kg)
        impeller_scaling: 100.0, // Standard impeller scaling (%)
        average_rating_over: 2, // Standard rating average (strokes)
        created_at: new Date().toISOString(),
      };
    },
    enabled: !!sessionId,
    staleTime: 10 * 60 * 1000, // 10 minutes (parameters change rarely)
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Cache invalidation utilities for chart data
export const useChartCache = () => {
  const { invalidateSession: _invalidateSession } = useSessionCache();
  const queryClient = useQueryClient();

  const invalidateChartData = (sessionId: string, chartType?: string) => {
    if (chartType) {
      // Invalidate specific chart type
      queryClient.invalidateQueries({
        queryKey: ['chart-data', chartType, sessionId],
      });
    } else {
      // Invalidate all chart data for session
      queryClient.invalidateQueries({
        queryKey: ['chart-data', sessionId],
      });
    }
  };

  const invalidateAllChartData = (sessionId: string) => {
    queryClient.invalidateQueries({
      queryKey: ['chart-data', sessionId],
    });
  };

  const prefetchChartData = (sessionId: string) => {
    // Prefetch all chart data types for better UX
    queryClient.prefetchQuery({
      queryKey: ['chart-data', 'power-time-series', sessionId],
      queryFn: () => fetchPowerTimeSeriesData(sessionId),
    });

    queryClient.prefetchQuery({
      queryKey: ['chart-data', 'performance-metrics', sessionId],
      queryFn: () => fetchPerformanceMetricsData(sessionId),
    });

    queryClient.prefetchQuery({
      queryKey: ['chart-data', 'comprehensive-session', sessionId],
      queryFn: () => telemetryService.getComprehensiveSessionData(sessionId),
    });
  };

  return {
    invalidateChartData,
    invalidateAllChartData,
    prefetchChartData,
  };
};
